// ============================================================
// Content-Preference Framework (CONTENT_TOGGLES.md) — single source of truth.
// Char-editor + pause surfaces both render a VERTICAL TOGGLE LIST from
// CONTENT_TRACKS: one row per track = name + short explanation + enable/disable
// toggle. No icons. Reserved slots show "(PLACEHOLDER)" + a disabled toggle so
// the framework reads as "can hold more" without promising a specific kink.
//
// ARMED 2026-07-08 (shared 0.3 kill-switch, both flags true):
//   - editor surface gates on CONTENT_PREFS_ENABLED (JS mirror of the Ink VAR,
//     flipped together at arm time, like MASC_ROUTE_ENABLED).
//   - pause surface reads the live Ink `v030_content_enabled` (contentMenuEnabled).
// Opt-in (pref_<id>) is only the player's preference; content still needs the
// full composition (killswitch && is_content_enabled && progression).
// ============================================================

import { L } from '../systems/localization.js';
import { isPat } from '../engine/pgate.js';

// Flipped true 2026-07-08 (arm), together with the Globals.ink `v030_content_enabled` default.
export const CONTENT_PREFS_ENABLED = true;

const PLACEHOLDER_LABEL = '(PLACEHOLDER)';

export const CONTENT_TRACKS = [
    // BNWO FREED 0.3.1 (gate audit 2026-07-23): isPatreon false so FREE
    // players see the toggle too — the opt-in is the whole consent model now.
    { id: 'bnwo', labelKey: 'ui.pref.bnwo.label', descKey: 'ui.pref.bnwo.desc', isPatreon: false, safeword: false },
    // Free Use track (0.3.1 poll #2). safeword:false — the per-scene out IS the
    // anklet (unequip hides the whole track). Whole track Patreon (owner 2026-07-16).
    { id: 'free_use', labelKey: 'ui.pref.free_use.label', descKey: 'ui.pref.free_use.desc', isPatreon: true, safeword: false },
    // Pet Play (0.3.1 poll #1, KENNEL_EXPANSION.md §10.2) — the first RETROFIT track:
    // its Ink pref_pet_play defaults TRUE (grandfather: old saves keep the free base
    // Kennel), the editor loop writes false for new games unless opted in. isPatreon
    // false — the base Kennel is free since 0.2.8; only the 0.3.1 expansion beats are
    // per-scene Patreon. safeword:false — Mira's in-fiction safeword system IS the out.
    { id: 'pet_play', labelKey: 'ui.pref.pet_play.label', descKey: 'ui.pref.pet_play.desc', isPatreon: false, safeword: false },
    // Reserved slots — the framework can hold more tracks. Labelled (PLACEHOLDER)
    // so no specific kink is promised; disabled until a real track + its content
    // lands (then give it a real id/labelKey/descKey and drop `placeholder`).
    { id: 'placeholder_c', placeholder: true },
];

function _l(key, fallback) {
    try { const v = L.get(key); return (v && v !== key) ? v : (fallback || key); }
    catch (e) { return fallback || key; }
}

// Live read of the shared 0.3 kill-switch (pause surface; story exists mid-run).
export function contentMenuEnabled(story) {
    try { return !!(story && story.variablesState['v030_content_enabled']); }
    catch (e) { return false; }
}

// Patreon-only tracks are HIDDEN entirely from free players (not shown disabled),
// so a free player configures nothing they can't reach. Callers hide the whole
// section when this is false. `free` = !isPat().
export function hasVisibleContentTracks(free) {
    return CONTENT_TRACKS.some(t => !t.placeholder && !(t.isPatreon && free));
}

// One toggle-list row: name + (explanation) + a toggle. Placeholders are a
// disabled toggle with the "(PLACEHOLDER)" label and no description.
function _rowHtml(track, { checked, locked, idPrefix }) {
    const ph = !!track.placeholder;
    const name = ph ? PLACEHOLDER_LABEL : _l(track.labelKey, track.id);
    const desc = ph ? '' : _l(track.descKey, '');
    const disabled = ph || locked;
    const inputId = `${idPrefix}-${track.id}`;
    const patreonHint = (!ph && locked) ? ' <span class="pref-patreon">(Patreon)</span>' : '';
    return `
        <div class="pref-row${ph ? ' pref-placeholder' : ''}">
            <div class="pref-info">
                <div class="pref-name">${name}${patreonHint}</div>
                ${desc ? `<div class="pref-desc">${desc}</div>` : ''}
            </div>
            <label class="toggle-switch">
                <input type="checkbox" id="${inputId}"${ph ? '' : ` data-pref-var="pref_${track.id}"`}${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}>
                <span class="toggle-slider"></span>
            </label>
        </div>`;
}

// ── Char-editor surface (new game). Renders the toggle list into the container;
// flipping a real track's toggle pushes/pops its id in editorState.contentPrefs.
export function buildContentPrefCards(selectedPrefs, containerSelector) {
    const box = document.querySelector(containerSelector);
    if (!box) return;
    const free = !isPat();
    // Patreon-only tracks are hidden entirely from free players (not shown disabled).
    const visible = CONTENT_TRACKS.filter(t => !(t.isPatreon && free));
    box.innerHTML = visible.map(t => _rowHtml(t, {
        checked: selectedPrefs.includes(t.id),
        locked: t.isPatreon && free,
        idPrefix: 'editor-pref',
    })).join('');
    for (const track of visible) {
        if (track.placeholder) continue;
        const input = box.querySelector(`#editor-pref-${track.id}`);
        if (!input || input.disabled) continue;
        input.addEventListener('change', () => {
            const i = selectedPrefs.indexOf(track.id);
            if (input.checked && i < 0) selectedPrefs.push(track.id);
            else if (!input.checked && i >= 0) selectedPrefs.splice(i, 1);
        });
    }
}

// ── Pause-overlay surface (mid-run). Same toggle list; change -> the Ink pref
// VAR. Toggling OFF auto-mutes the track's morning SMS (CONTENT_TOGGLES.md §5).
export function initContentPrefToggles(story) {
    const list = document.getElementById('content-prefs-list');
    if (!list) return;
    const free = !isPat();
    // Patreon-only tracks are hidden entirely from free players (not shown disabled).
    const visible = CONTENT_TRACKS.filter(t => !(t.isPatreon && free));
    list.innerHTML = visible.map(t => {
        let checked = false;
        try { checked = !t.placeholder && !!(story && story.variablesState[`pref_${t.id}`]); } catch (e) {}
        return _rowHtml(t, { checked, locked: t.isPatreon && free, idPrefix: 'pause-pref' });
    }).join('');
    for (const track of visible) {
        if (track.placeholder) continue;
        const input = list.querySelector(`#pause-pref-${track.id}`);
        if (!input || input.disabled) continue;
        input.addEventListener('change', () => {
            try { story.variablesState[`pref_${track.id}`] = input.checked; } catch (e) {}
            // Mirror the opt-out into the SMS-mute VAR (framework: mute_<id>_sms).
            // Bidirectional so re-opting-in CLEARS the mute and the invite can fire
            // again — the read side is wired for bnwo (MorningEvents.ink). try/catch:
            // not every track declares a mute VAR.
            try { story.variablesState[`mute_${track.id}_sms`] = !input.checked; } catch (e) {}
        });
    }
}
